/** @jsx jsx */

import React, { useEffect, useMemo } from "react";
import { jsx } from "@emotion/core";
import { Formik, Form, FormikHelpers } from "formik";
import { Prompt } from "react-router-dom";

//component, hooks
import Button from "../../../components/button";
import { FormDropdown, FormInputNumber } from "../../../components/formHelpers";
import useCustomerList from "../../../hooks/eligibility/useCustomerList";
import useLoanStatus from "../../../hooks/auth/useLoanStatus";
import useRedirect from "../../../helperHooks/useRedirect";

import { commonFoldStyles } from "../EligibilityViewStyles";
import { EligibilityRoutes } from "../../../Routes";
import { SaveEligibilityRequest } from "../../../store/eligibility/actions";
import { EligibilityDetailsState } from "../../../store/eligibility/state";
import { Receivable } from "../../../services/eligibilityService/EligibilityService.type";
import {
  receivablesDetailsFoldConstants as CONSTANTS,
  receivablesDetailsFoldValidationSchema as validationSchema
} from "./ReceivablesDetailsFoldConstants";
import { unsavedChangeAlert } from "../../../Constants";

interface Props {
  eligibilityDetails: EligibilityDetailsState;
  submitting: boolean;
  saveEligibilityDetails: (data: SaveEligibilityRequest) => void;
}

export interface ReceivablesDetailState {
  receivablesDetails: Receivable[];
}

const ReceivablesDetailsFold: React.FC<Props> = (props: Props) => {
  const {
    eligibilityDetails: { receivablesDetails },
    saveEligibilityDetails,
    submitting
  } = props;

  const initialValues = {
    receivablesDetails
  };

  const redirect = useRedirect();
  const { getCustomerList, customerListData } = useCustomerList();

  const { disableEligibilityForms } = useLoanStatus();

  useEffect(() => {
    getCustomerList();
  }, [getCustomerList]);

  const customerList = useMemo(() => {
    const customers = {};
    //Formatting customers list for Dropdown
    customerListData.data.forEach(
      customer =>
        (customers[`${customer.tradeName || ""} - ${customer.gstin}`] =
          customer.gstin)
    );
    return customers;
  }, [customerListData.data]);

  const handleSubmit = (
    data: ReceivablesDetailState,
    formikHelpers: FormikHelpers<ReceivablesDetailState>
  ) => {
    saveEligibilityDetails({
      data: {
        receivablesDetails: data.receivablesDetails.map(item => ({
          gstin: item.gstin,
          receivableAmountForNextFourMonths:
            item.receivableAmountForNextFourMonths
        }))
      },
      onSuccess: () => {
        //to reset initial state, so that, form is not "dirty"
        formikHelpers.resetForm();
        redirect(routes =>
          routes.eligibility.view(EligibilityRoutes.EligibilityAmountFold)
        );
      }
    });
  };

  return (
    <div css={commonFoldStyles}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
      >
        {({ dirty }) => (
          <Form>
            <Prompt when={dirty} message={unsavedChangeAlert} />
            <div className="grid-container">
              <FormDropdown
                name="receivablesDetails[0].gstin"
                className="mb-3 mb-md-0"
                label={CONSTANTS.labels.gstin}
                placeholder={CONSTANTS.placeholders.gstin}
                loading={customerListData.loading}
                options={customerList}
                disable={disableEligibilityForms}
                mandatory
              />
              <FormInputNumber
                name="receivablesDetails[0].receivableAmountForNextFourMonths"
                className="mb-3 mb-md-0"
                mandatory
                label={CONSTANTS.labels.receivableAmountForNextFourMonths}
                disabled={disableEligibilityForms}
                precision={2}
              />
            </div>
            {!disableEligibilityForms && (
              <Button
                htmlType="submit"
                type="primary"
                disabled={submitting}
                loading={submitting}
                containerDivClassName="justify-content-md-end"
                className="mt-5"
              >
                {CONSTANTS.buttons.next}
              </Button>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ReceivablesDetailsFold;
